import './CopWcnDocuments.scss'

import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  fetchAllowedProductFamiliesInOrder,
  fetchHtmlDocumentationByFamily,
  openHtmlDocumentation,
} from '../../services/resources'

export interface WcnDocument {
  label: string
  dataKey: string
}

export interface WcnDocumentTab {
  id: string
  label: string
}

const toTabId = (family: string): string =>
  family
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const buildTabs = (families: string[]): WcnDocumentTab[] =>
  families
    .filter(family => typeof family === 'string' && family.trim() !== '')
    .map(family => ({
      id: toTabId(family),
      label: family.trim(),
    }))

const toDocumentLabel = (id: string): string => {
  const words = id
    .split('-')
    .map(part => part.trim())
    .filter(part => part !== '' && part.toLowerCase() !== 'doc')
    .map(part => part.toUpperCase())
  return `${words.join(' ')} Documentation`
}

const buildDocuments = (ids: string[]): WcnDocument[] =>
  ids
    .filter(id => typeof id === 'string' && id.trim() !== '')
    .map(id => ({ label: toDocumentLabel(id), dataKey: id.trim() }))

const MIN_LOADER_MS = 2500

const CopWcnDocuments = () => {
  const [tabs, setTabs] = useState<WcnDocumentTab[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTabId, setActiveTabId] = useState<string>('')
  const [documentsByTab, setDocumentsByTab] = useState<Record<string, WcnDocument[]>>({})
  const [loadingTabId, setLoadingTabId] = useState<string | null>(null)
  const [openingKey, setOpeningKey] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    fetchAllowedProductFamiliesInOrder()
      .then(families => {
        if (!isMounted) {
          return
        }
        const nextTabs = buildTabs(families)
        setTabs(nextTabs)
        setActiveTabId(nextTabs[0]?.id ?? '')
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })
    return () => {
      isMounted = false
    }
  }, [])

  const activeTab = useMemo(
    () => tabs.find(tab => tab.id === activeTabId) ?? tabs[0],
    [tabs, activeTabId],
  )

  // Fetch (and cache) the documentation links for the active tab's family.
  useEffect(() => {
    if (!activeTab || documentsByTab[activeTab.id]) {
      return
    }
    let isMounted = true
    setLoadingTabId(activeTab.id)
    fetchHtmlDocumentationByFamily(activeTab.label)
      .then(ids => {
        if (!isMounted) {
          return
        }
        setDocumentsByTab(prev => ({ ...prev, [activeTab.id]: buildDocuments(ids) }))
      })
      .finally(() => {
        if (isMounted) {
          setLoadingTabId(current => (current === activeTab.id ? null : current))
        }
      })
    return () => {
      isMounted = false
    }
  }, [activeTab, documentsByTab])

  const handleDocumentActivate = useCallback(
    async (document: WcnDocument) => {
      if (!document.dataKey || openingKey) {
        return
      }
      setOpeningKey(document.dataKey)
      const startedAt = Date.now()
      try {
        await openHtmlDocumentation(document.dataKey)
      } finally {
        const elapsed = Date.now() - startedAt
        const remaining = MIN_LOADER_MS - elapsed
        if (remaining > 0) {
          await new Promise(resolve => window.setTimeout(resolve, remaining))
        }
        setOpeningKey(null)
      }
    },
    [openingKey],
  )

  if (isLoading) {
    return (
      <div className="cmp-cop-wcn-documents cmp-cop-wcn-documents--loading">
        <span className="cmp-cop-wcn-documents__spinner" aria-hidden="true" />
      </div>
    )
  }

  if (tabs.length === 0) {
    return null
  }

  const activeDocuments = activeTab ? documentsByTab[activeTab.id] : undefined
  const isTabLoading = activeTab ? loadingTabId === activeTab.id : false

  return (
    <div className="cmp-cop-wcn-documents">
      <div className="cmp-cop-wcn-documents__tabs" role="tablist">
        {tabs.map(tab => {
          const isActive = tab.id === activeTab?.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`cmp-cop-wcn-documents__tab${isActive ? ' cmp-cop-wcn-documents__tab--active' : ''}`}
              onClick={() => setActiveTabId(tab.id)}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      <ul className="cmp-cop-wcn-documents__list" role="tabpanel">
        {isTabLoading ? (
          <li className="cmp-cop-wcn-documents__loading">
            <span className="cmp-cop-wcn-documents__spinner" aria-hidden="true" />
          </li>
        ) : activeDocuments && activeDocuments.length ? (
          activeDocuments.map(document => {
            const isOpening = openingKey === document.dataKey
            return (
              <li key={document.dataKey} className="cmp-cop-wcn-documents__item">
                <button
                  type="button"
                  className="cmp-cop-wcn-documents__link"
                  aria-busy={isOpening}
                  disabled={openingKey !== null && !isOpening}
                  onClick={() => handleDocumentActivate(document)}
                >
                  <span className="cmp-cop-wcn-documents__label">{document.label}</span>
                  {isOpening ? (
                    <span className="cmp-cop-wcn-documents__spinner" aria-hidden="true" />
                  ) : (
                    <span className="cmp-cop-wcn-documents__chevron" aria-hidden="true" />
                  )}
                </button>
              </li>
            )
          })
        ) : (
          <li className="cmp-cop-wcn-documents__empty">No documents available.</li>
        )}
      </ul>
    </div>
  )
}

export default CopWcnDocuments